import React, { FormEvent, useState } from 'react';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAppState } from '../../context/AppStateContext';

interface LoginPageProps {
  onLoginSuccess: (token: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const { login } = useAuth();
  const { appName } = useAppState();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const token = await login(username, password);
      onLoginSuccess(token); // Pass the token to parent
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="max-w-5xl w-full flex flex-col md:flex-row rounded-2xl shadow-2xl bg-white overflow-hidden">
        {/* Left side - illustration/welcome */}
        <div className="w-full md:w-1/2 bg-blue-800 p-8 md:p-12 text-white flex flex-col justify-between relative overflow-hidden">
          <div className="relative z-10 py-8 md:py-0">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-center md:text-left">
              ยินดีต้อนรับสู่
            </h2>
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center md:text-left">
              {appName} - ระบบจัดการแม่พิมพ์
            </h2>
          </div>

          {/* Decorative circles - hidden on very small screens */}
          <div className="hidden sm:block absolute bottom-0 right-0 w-64 h-64 bg-blue-700 rounded-full transform translate-x-1/2 translate-y-1/2 opacity-50"></div>
          <div className="hidden sm:block absolute top-0 left-0 w-32 h-32 bg-blue-700 rounded-full transform -translate-x-1/2 -translate-y-1/2 opacity-50"></div>
        </div>

        {/* Right side - login form */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-12">
          <div className="mb-6 md:mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 text-center md:text-left">
              เข้าสู่ระบบด้วยรหัสผ่าน
            </h3>
          </div>

          {error && (
            <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-sm md:text-base text-red-700">{error}</span>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-sm md:text-base font-medium text-gray-700">
                รหัสผู้ใช้
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-4 py-2.5 md:py-3 pl-11 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors text-sm md:text-base"
                  placeholder="กรอกรหัสผู้ใช้"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  required
                  data-cy="username-input"
                />
                <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm md:text-base font-medium text-gray-700">
                รหัสผ่าน
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-4 py-2.5 md:py-3 pl-11 pr-11 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors text-sm md:text-base"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  data-cy="password-input"
                />
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-2">
              <a
                href="#"
                className="text-sm text-blue-600 hover:text-blue-800 text-center sm:text-left"
              >
                ลืมรหัสผ่าน?
              </a>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-800 text-white py-2.5 md:py-3 px-4 rounded-lg hover:bg-blue-700 transform transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none text-sm md:text-base"
              disabled={isLoading}
            >
              {isLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
